/**
 * Catálogo das ferramentas do programa de embaixadores.
 *
 * As ferramentas em teste são liberadas uma por vez, na ordem desta lista: a
 * próxima abre na data do pacote dela e com o primeiro feedback da anterior, e
 * cada uma fica disponível por alguns dias depois do "Iniciar teste". A ordem,
 * as datas e o prazo valem no backend (Ambassador::TOOL_RELEASES e TEST_DAYS),
 * que manda a situação de cada ferramenta no perfil; incluir ou reordenar uma
 * ferramenta exige mudar os dois lados (e o espelho do catálogo no painel do CRM).
 *
 * Tudo o que está neste arquivo vai no bundle e é legível por qualquer
 * visitante, embaixador ou não.
 */
export interface AmbassadorToolCard {
  /** Minúsculas, números e hífens; é o identificador gravado com o feedback. */
  slug: string;
  name: string;
  summary: string;
  /** Chave do ícone em services/featureIcon, a mesma de "Nossas Ferramentas". */
  icon?: string;
}

export interface AmbassadorTool extends AmbassadorToolCard {
  /**
   * Rota interna aberta em "Iniciar teste". Sem ela a ferramenta ainda não
   * está pronta: o card fica bloqueado, mesmo depois da data de liberação.
   */
  link?: string;
  /** Texto abaixo do vídeo; sem ele, a página usa o resumo. */
  description?: string;
  /**
   * URL de incorporação do vídeo explicativo. Com vídeo do Vimeo
   * (https://player.vimeo.com/video/123), "Iniciar teste" só é liberado depois
   * que o vídeo chega ao fim; outros players não avisam o fim e deixam o botão livre.
   */
  embedUrl?: string;
  directorName?: string;
  /** Link https://wa.me/55... do botão "Falar sobre o projeto"; fica público no bundle. */
  directorWhatsappUrl?: string;
}

/** Dias de uso depois do "Iniciar teste"; o mesmo Ambassador::TEST_DAYS do backend. */
export const AMBASSADOR_TEST_DAYS = 10;

export const DR_LICITA_AMBASSADOR_SLUG = 'dr-licita-3';
export const CENTRAL_IA_AMBASSADOR_SLUG = 'central-ia';
export const CONTRACT_CLAUSES_AMBASSADOR_SLUG = 'analise-de-clausulas-contratuais';
// A Análise de Mercado 3.0 é a antiga Análise de Preços: o slug ficou o mesmo.
export const PRICE_INTELLIGENCE_AMBASSADOR_SLUG = 'analise-de-precos';

/** Ferramentas em teste, na ordem em que são liberadas. */
export const AMBASSADOR_TOOLS: AmbassadorTool[] = [
  {
    slug: DR_LICITA_AMBASSADOR_SLUG,
    name: 'Dr. Licita 3.0',
    summary: 'Elabore petições com o apoio de uma IA especializada em contratações públicas.',
    link: '/dr_licita/chat',
    icon: 'dr_licita',
    directorName: 'Bruno Moura',
  },
  {
    slug: CENTRAL_IA_AMBASSADOR_SLUG,
    name: 'Central IA',
    summary: 'Acesse 10 dos principais modelos de IA generativa em um só lugar, com uso ilimitado.',
    link: '/central_ia',
    icon: 'central_ia',
  },
  {
    slug: CONTRACT_CLAUSES_AMBASSADOR_SLUG,
    name: 'Análise de Cláusulas Contratuais',
    summary:
      'Identifique divergências antes de assinar: compare as cláusulas da minuta do edital com as do contrato enviado pela administração pública.',
    link: '/validar_contrato',
    icon: 'analyze_contract',
  },
  {
    slug: PRICE_INTELLIGENCE_AMBASSADOR_SLUG,
    name: 'Análise de Mercado 3.0',
    summary:
      'Conheça os preços praticados, as empresas do segmento e as contratações futuras para planejar sua atuação e identificar novas oportunidades de negócio.',
    link: '/pesquisa_de_precos',
    icon: 'price_intelligence',
  },
  {
    slug: 'boletim-3',
    name: 'Boletim 3.0',
    summary:
      'Selecione oportunidades com mais praticidade no novo Boletim de Licitações. Conte com os novos filtros de busca e os recursos de inteligência artificial: Filtrar com IA e Licitação Compatível.',
    icon: 'bulletins',
  },
  {
    slug: 'encontrar-licitacao-3',
    name: 'Encontrar 3.0',
    summary:
      'Tenha mais precisão nas suas buscas com o novo Encontrar Licitações. Explore os recursos Filtrar com IA e Licitação Compatível.',
    icon: 'search',
  },
  {
    slug: 'gestao-ia-modulo-1',
    name: 'Gestão IA – Módulo I',
    summary:
      'Organize as oportunidades em um kanban, gerencie as tarefas da equipe e acompanhe as atualizações das licitações.',
    icon: 'ia',
  },
  {
    slug: 'gestao-ia-modulo-2',
    name: 'Gestão IA – Módulo II',
    summary:
      'Estruture seu planejamento e avalie as oportunidades para decidir, com mais segurança, de quais licitações participar.',
    icon: 'ia',
  },
  {
    slug: 'gestao-ia-modulo-3',
    name: 'Gestão IA – Módulo III',
    summary:
      'Atue com mais agilidade e precisão na sessão pública com ferramentas de automação de lances e monitoramento do chat.',
    icon: 'ia',
  },
  {
    slug: 'gestao-ia-modulo-4',
    name: 'Gestão IA – Módulo IV',
    summary:
      'Organize o pós-venda e acompanhe as demandas dessa etapa para dar continuidade ao relacionamento com os órgãos públicos.',
    icon: 'ia',
  },
];

export const findAmbassadorTool = (slug: string): AmbassadorTool | undefined =>
  AMBASSADOR_TOOLS.find((tool) => tool.slug === slug);
